// RFC 6962 Merkle tree (Certificate Transparency convention).
// - leaf_hash(d) = SHA-256(0x00 || d)
// - node_hash(l, r) = SHA-256(0x01 || l || r)
// - For n > 1 leaves, k = largest power of 2 < n; left subtree = D[0:k], right = D[k:n].
//   No duplication of odd nodes — trees are unbalanced.
//
// Pure functions, no I/O. Used both server-side (apps/api builds trees over
// stamp batches) and client-side (verifier reconstructs the root from a
// leaf + audit path to confirm inclusion).
//
// `MerkleStep` is re-exported from `@filevouch/receipt` (canonical source);
// we only consume it as a type here.

import type { MerkleStep } from "@filevouch/receipt";

const LEAF_PREFIX = new Uint8Array([0x00]);
const NODE_PREFIX = new Uint8Array([0x01]);

export type MerkleResult = {
  root: string; // 64-char lowercase hex
  paths: MerkleStep[][]; // one per input leaf, in input order
};

function hexToBytes(hex: string): Uint8Array {
  if (!/^[0-9a-f]*$/.test(hex)) {
    throw new Error("hex string must contain only [0-9a-f]");
  }
  if (hex.length % 2 !== 0) {
    throw new Error("hex length must be even");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

async function sha256(...parts: Uint8Array[]): Promise<Uint8Array> {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const buf = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    buf.set(p, off);
    off += p.length;
  }
  // Cast: TS 5.7's Uint8Array<ArrayBufferLike> isn't assignable to
  // BufferSource without narrowing. We never get SharedArrayBuffer here.
  const digest = await crypto.subtle.digest("SHA-256", buf as BufferSource);
  return new Uint8Array(digest);
}

async function leafHash(data: Uint8Array): Promise<Uint8Array> {
  return sha256(LEAF_PREFIX, data);
}

async function nodeHash(
  left: Uint8Array,
  right: Uint8Array,
): Promise<Uint8Array> {
  return sha256(NODE_PREFIX, left, right);
}

async function mth(leaves: Uint8Array[]): Promise<Uint8Array> {
  const n = leaves.length;
  if (n === 0) return sha256();
  if (n === 1) return leafHash(leaves[0]!);
  let k = 1;
  while (k * 2 < n) k *= 2;
  const left = await mth(leaves.slice(0, k));
  const right = await mth(leaves.slice(k));
  return nodeHash(left, right);
}

async function pathFor(
  leaves: Uint8Array[],
  m: number,
): Promise<MerkleStep[]> {
  const n = leaves.length;
  if (n <= 1) return [];
  let k = 1;
  while (k * 2 < n) k *= 2;
  if (m < k) {
    const inner = await pathFor(leaves.slice(0, k), m);
    const sibling = await mth(leaves.slice(k));
    return [...inner, { hash: bytesToHex(sibling), position: "right" }];
  } else {
    const inner = await pathFor(leaves.slice(k), m - k);
    const sibling = await mth(leaves.slice(0, k));
    return [...inner, { hash: bytesToHex(sibling), position: "left" }];
  }
}

// Build the Merkle root + every leaf's inclusion path in one pass.
// Inputs are 64-char lowercase hex hashes.
export async function buildMerkleTree(
  hashHexes: string[],
): Promise<MerkleResult> {
  if (hashHexes.length === 0) {
    return { root: bytesToHex(await sha256()), paths: [] };
  }
  const leaves = hashHexes.map(hexToBytes);
  const root = await mth(leaves);
  const paths = await Promise.all(leaves.map((_, i) => pathFor(leaves, i)));
  return { root: bytesToHex(root), paths };
}

// Verify an inclusion proof: recompute the root from a leaf hash + path and
// compare against the expected root.
export async function verifyInclusion(
  leafHashHex: string,
  path: MerkleStep[],
  rootHex: string,
): Promise<boolean> {
  let current = await leafHash(hexToBytes(leafHashHex));
  for (const step of path) {
    const sibling = hexToBytes(step.hash);
    current =
      step.position === "left"
        ? await nodeHash(sibling, current)
        : await nodeHash(current, sibling);
  }
  return bytesToHex(current) === rootHex;
}
